/**
 * ═══════════════════════════════════════════════════════════════════════════
 * GRESHAM FORESTRY MAP - Google Maps with GeoJSON Integration
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * PURPOSE
 * -------
 * Displays forestry locations across the UK using Google Maps Advanced Markers
 * with custom markers and clustering. Data is fetched from WordPress REST API as GeoJSON.
 *
 * KEY FEATURES
 * ------------
 * - UK-restricted map viewport with min/max zoom constraints
 * - GeoJSON support for both Point features (markers) and non-Point features (polygons)
 * - Custom SVG marker icons with active state styling
 * - Marker clustering with consistent visual appearance
 * - Off-canvas panel integration (Elementor) for detailed marker information
 * - Active marker highlighting and viewport panning on click
 *
 * DEPENDENCIES
 * ------------
 * Required:
 * - Google Maps JavaScript API (loaded via bootstrap code below)
 * - @googlemaps/markerclusterer library (enqueued by gresham.php)
 * - kam_ui_object.rest_request() - custom AJAX wrapper
 * - jwt.get_map_file_jwt - JWT token for authenticated API requests
 * - window.KCSS_OffCanvas - off-canvas controller (from main.js)
 *
 * Optional (from $theme / assets / js / main.js context):
 * - createMarkerElement() - creates custom marker DOM
 * - setActiveMarker() - manages active marker state
 * - clearActiveMarker() - clears active marker on off-canvas close
 * - bindClearActiveOnOffCanvasClose() - wires off-canvas close handler
 * - updateMapOffCanvas() - populates off-canvas with marker data
 *
 * INTEGRATION POINTS
 * ------------------
 * 1. HTML: Requires <gmp-map> element in forestry_map.twig template
 * 2. WordPress: REST endpoint /wp-json/gresham/v1/get-map-file/
 * 3. Elementor: Off-canvas widget with aria-label="Marker Info"
 * 4. PHP: gresham.php enqueues this script on specific page IDs (1770, 20)
 *
 * EXECUTION FLOW
 * --------------
 * 1. window.load event fires
 * 2. Google Maps API bootstrap loads library
 * 3. initMap() is called automatically by Google's callback
 * 4. Map initializes, fetches GeoJSON, renders markers
 * 5. User interactions trigger off-canvas and marker highlighting
 *
 * ═══════════════════════════════════════════════════════════════════════════
 */

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * BOOTSTRAP: GOOGLE MAPS API LOADER
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * This self-executing function wraps on window.load to ensure DOM is ready.
 * It's the ENTRY POINT for the entire map system.
 *
 * WHAT IT DOES
 * ------------
 * 1. Waits for page to fully load (including images, CSS)
 * 2. Dynamically injects Google Maps API script tag
 * 3. Configures API key and version
 * 4. Sets up callback that triggers initMap() when API is ready
 *
 * WHY THIS APPROACH
 * -----------------
 * - Async loading: Doesn't block page rendering
 * - Dynamic: API key can be changed without editing HTML
 * - Version control: Uses "weekly" release channel for stability
 * - Library management: Only loads "maps" and "marker" libraries (smaller payload)
 *
 * GOOGLE MAPS API KEY
 * -------------------
 * Current key: AIzaSyCMIdXiKvYdLR6YWxHYVZgGSLxgEw06qQM
 *
 * To change the API key:
 * 1. Go to Google Cloud Console: https://console.cloud.google.com/
 * 2. Enable Maps JavaScript API for your project
 * 3. Create/copy an API key with Maps JavaScript API enabled
 * 4. Replace the key in the config object below
 * 5. Optional: Add domain restrictions for security
 *
 * API KEY RESTRICTIONS (RECOMMENDED) TODO: Review with Pete
 * ----------------------------------
 * In production, restrict this key to your domain:
 * - Application restrictions: HTTP referrers
 * - Website restrictions: yourdomain.com/*
 * - API restrictions: Maps JavaScript API only
 *  - OR
 *  - use localize in plugin php to set it there?
 *
 * VERSION CHANNELS
 * ----------------
 * - "weekly": Latest stable (recommended, auto-updates)
 * - "quarterly": Less frequent updates
 * - "3.54": Specific version (for stability, but no bug fixes)
 *
 * TROUBLESHOOTING (common errors)
 * ---------------
 * If map doesn't load:
 * 1. Check browser console for "API key" errors
 * 2. Verify key is enabled in Google Cloud Console
 * 3. Check domain restrictions aren't blocking localhost/staging
 * 4. Confirm billing is enabled (required for Maps API)
 * 5. Follow DEBUG below
 *
 * ═══════════════════════════════════════════════════════════════════════════
 */

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * DEBUG CONFIGURATION (GLOBAL SCOPE)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * IMPORTANT: This must be in global scope so runtime override works.
 *
 * HOW TO USE
 * ----------
 * Option 1 - Code change (requires rebuild):
 *   Set DEBUG to true below
 *
 * Option 2 - Runtime override (no rebuild needed):
 *   In browser console: window.GRESHAM_MAP_DEBUG = true;
 *   Then reload page
 *
 * Option 3 - Programmatic control:
 *   window.GreshamMap.setDebug(true);
 */
const GRESHAM_MAP_CONFIG = {
    DEBUG: false  // Set to true for development/debugging
};

/**
 * ───────────────────────────────────────────────────────────────────────────
 * DEBUG LOGGING HELPERS (GLOBAL SCOPE)
 * ───────────────────────────────────────────────────────────────────────────
 *
 * These must be in global scope to access window.GRESHAM_MAP_DEBUG correctly.
 */

function greshamMapLog() {
    if (!GRESHAM_MAP_CONFIG.DEBUG && !window.GRESHAM_MAP_DEBUG) return;
    console.log.apply(console, ['[Gresham Map]'].concat([].slice.call(arguments)));
}

function greshamMapWarn() {
    // if (!GRESHAM_MAP_CONFIG.DEBUG && !window.GRESHAM_MAP_DEBUG) return;
    console.warn.apply(console, ['[Gresham Map]'].concat([].slice.call(arguments)));
}

function greshamMapError() {
    // Errors always log, even when DEBUG is false
    console.error.apply(console, ['[Gresham Map]'].concat([].slice.call(arguments)));
}

function greshamMapLogGroup(title, fn) {
    const debugOn = !!(GRESHAM_MAP_CONFIG.DEBUG || window.gresham_MAP_DEBUG);
    // Always execute the wrapped code
    if (!debugOn) return fn();

    console.group('[Gresham Map] ' + title);
    try {
        return fn();
    } finally {
        console.groupEnd();
    }
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * PUBLIC API (Exposed before initialization)
 * ═══════════════════════════════════════════════════════════════════════════
 */
window.GreshamMap = {
    /**
     * Enable/disable debug logging at runtime
     * @param {boolean} val - true to enable, false to disable
     *
     * Example:
     *   GreshamMap.setDebug(true);  // Enable
     *   GreshamMap.setDebug(false); // Disable
     */
    setDebug: function(val) {
        window.GRESHAM_MAP_DEBUG = !!val;
        greshamMapLog('Debug logging ' + (val ? 'enabled' : 'disabled'));
    }
};

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * BOOTSTRAP: GOOGLE MAPS API LOADER
 * ═══════════════════════════════════════════════════════════════════════════
 */
window.addEventListener("load", () => {
    (g => {
        var h, a, k, p = "The Google Maps JavaScript API", c = "google", l = "importLibrary", q = "__ib__",
            m = document, b = window;
        b = b[c] || (b[c] = {});
        var d = b.maps || (b.maps = {}), r = new Set, e = new URLSearchParams,
            u = () => h || (h = new Promise(async (f, n) => {
                await (a = m.createElement("script"));
                e.set("libraries", [...r] + "");
                for (k in g) e.set(k.replace(/[A-Z]/g, t => "_" + t[0].toLowerCase()), g[k]);
                e.set("callback", c + ".maps." + q);
                a.src = `https://maps.${c}apis.com/maps/api/js?` + e;
                d[q] = f;
                a.onerror = () => h = n(Error(p + " could not load."));
                a.nonce = m.querySelector("script[nonce]")?.nonce || "";
                m.head.append(a);
            }));
        d[l] ? console.warn(p + " only loads once. Ignoring:", g) : d[l] = (f, ...n) => r.add(f) && u().then(() => d[l](f, ...n))
    })
    ({ key: "AIzaSyCMIdXiKvYdLR6YWxHYVZgGSLxgEw06qQM", v: "weekly"});

    // ═════════════════════════════════════════════════════════════════════
    // HELPER FUNCTIONS (using global log functions)
    // ═════════════════════════════════════════════════════════════════════

    window.__activeMapMarkerElement = null;
    elementorFrontend.hooks.addAction(
        'frontend/element_handler_destroyed/global',
        h => console.log('Destroyed:', h)
    );
    function setMapFieldText(panel, key, value) {
        const wrapper = panel.querySelector(`[data-map-field="${key}"]`);
        if (!wrapper) {
            greshamMapWarn(`Off-canvas field not found: data-map-field="${key}"`);
            return false;
        }

        const text = value == null ? "" : String(value);
        greshamMapLog(`Updating off-canvas field "${key}":`, text);

        const heading = wrapper.querySelector(".elementor-heading-title");
        if (heading) {
            heading.textContent = text;
            greshamMapLog(`Updated heading widget for "${key}"`);
            return true;
        }

        const textEditor = wrapper.querySelector(".elementor-widget-container");
        if (textEditor && textEditor.childNodes.length) {
            replaceTextNodes(textEditor, text);
            greshamMapLog(`Updated text editor widget for "${key}"`);
            return true;
        }

        replaceTextNodes(wrapper, text);
        greshamMapLog(`Updated wrapper text nodes for "${key}"`);
        return true;
    }

    function replaceTextNodes(root, newText) {
        const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
        let firstSet = false;

        while (walker.nextNode()) {
            const node = walker.currentNode;
            if (!node.nodeValue || !node.nodeValue.trim()) continue;

            if (!firstSet) {
                node.nodeValue = newText;
                firstSet = true;
            } else {
                node.nodeValue = "";
            }
        }

        if (!firstSet) {
            root.appendChild(document.createTextNode(newText));
        }
    }

    function updateMapOffCanvas(feature, offCanvasName) {
        greshamMapLogGroup('Update Off-Canvas', () => {
            greshamMapLog('Off-canvas name:', offCanvasName);
            greshamMapLog('Feature data:', feature);

            const panel = document.querySelector(
                `.e-off-canvas[aria-label="${CSS.escape(offCanvasName)}"]`
            );

            if (!panel) {
                greshamMapWarn(`Off-canvas panel not found: "${offCanvasName}"`);
                return false;
            }

            const props = feature?.properties || {};
            const coords = feature?.geometry?.coordinates || [];

            const title = props.Name || "Location";
            const area = props.SUM_AreaHa != null ? Number(props.SUM_AreaHa).toFixed(2) : "";
            const lat = coords.length >= 2 ? coords[1] : "";
            const lng = coords.length >= 2 ? coords[0] : "";

            greshamMapLog('Extracted data:', { title, area, lat, lng });

            const setText = (key, value) => setMapFieldText(panel, key, value);

            setText("title", title);
            setText("area", area ? `${area} (ha)` : "");
            // setText("coords", (lat !== "" && lng !== "") ? `Lat/Lng: ${lat}, ${lng}` : "");
            setText("coords-lat", (lat !== "") ? `${lat}` : "");
            setText("coords-long", (lng !== "") ? `${lng}` : "");

            greshamMapLog('Off-canvas updated successfully');
        });

        return true;
    }

    function createMarkerElement(feature) {
        const name = feature?.properties?.Name || 'Unknown';
        greshamMapLog(`Creating marker element for: ${name}`);

        const el = document.createElement("div");
        el.className = "kcss_map-marker";

        el.innerHTML = `
        <span class="kcss_map-marker__icon">
            <svg xmlns="http://www.w3.org/2000/svg"
                 viewBox="0 0 24 24"
                 width="24"
                 height="24"
                 class="kcss_map-marker__svg">
                <path id="Path_379-2" data-name="Path 379" d="M33.648,38.206a5.912,5.912,0,0,0,3.865,1.3A6.041,6.041,0,0,0,43.3,33.553a5.938,5.938,0,0,0-3.723-5.492,6.309,6.309,0,0,0-11.851,0A5.938,5.938,0,0,0,24,33.557a6.042,6.042,0,0,0,5.784,5.952A5.911,5.911,0,0,0,33.648,38.206Z" transform="translate(-21.773 -21.698)" fill="oklch(from currentColor 100% 0% h / 0.9)"></path>
                <path id="Line_28" data-name="Line 28" d="M0,13.833a.5.5,0,0,1-.5-.5V0A.5.5,0,0,1,0-.5.5.5,0,0,1,.5,0V13.333A.5.5,0,0,1,0,13.833Z" transform="translate(12 8)" fill="currentColor"></path>
                <path id="Line_29" data-name="Line 29" d="M4,3.167a.5.5,0,0,1-.277-.084l-4-2.667A.5.5,0,0,1-.416-.277.5.5,0,0,1,.277-.416l4,2.667A.5.5,0,0,1,4,3.167Z" transform="translate(8 12)" fill="currentColor"></path>
                <path id="Line_30" data-name="Line 30" d="M0,3.167a.5.5,0,0,1-.416-.223.5.5,0,0,1,.139-.693l4-2.667a.5.5,0,0,1,.693.139.5.5,0,0,1-.139.693l-4,2.667A.5.5,0,0,1,0,3.167Z" transform="translate(12 9.333)" fill="currentColor"></path>
                <path id="Path_380" data-name="Path 380" d="M29.944,40.011l-.174,0a6.44,6.44,0,0,1-2.438-12.326,6.809,6.809,0,0,1,12.632,0,6.452,6.452,0,0,1-2.438,12.322l-.178,0a6.437,6.437,0,0,1-3.7-1.175A6.434,6.434,0,0,1,29.944,40.011Zm3.7-15.594a5.828,5.828,0,0,0-5.456,3.815.5.5,0,0,1-.283.292A5.415,5.415,0,0,0,24.5,33.558a5.537,5.537,0,0,0,5.3,5.451l.147,0a5.428,5.428,0,0,0,3.391-1.194.5.5,0,0,1,.626,0A5.429,5.429,0,0,0,37.5,39.005a5.536,5.536,0,0,0,5.3-5.451,5.415,5.415,0,0,0-3.409-5.03.5.5,0,0,1-.283-.292A5.828,5.828,0,0,0,33.648,24.417Z" transform="translate(-21.773 -21.698)" fill="currentColor"></path>
            </svg>
        </span>
    `;
        return el;
    }

    let KCSS_ACTIVE_MARKER_EL = null;
    let KCSS_INACTIVE_MARKER_ZINDEX = null;

    function setActiveMarker(markerItem, markerIndex, totalMarkerCount) {

        if (KCSS_ACTIVE_MARKER_EL && KCSS_ACTIVE_MARKER_EL !== markerItem) {
            greshamMapLog('Removing active state from previous marker');
            KCSS_ACTIVE_MARKER_EL.content.classList.remove("is-active");
            KCSS_ACTIVE_MARKER_EL.zIndex = KCSS_INACTIVE_MARKER_ZINDEX;
        }
        KCSS_ACTIVE_MARKER_EL = markerItem || null;
        KCSS_INACTIVE_MARKER_ZINDEX = markerIndex || null;
        if (KCSS_ACTIVE_MARKER_EL) {
            greshamMapLog('Setting active marker');
            KCSS_ACTIVE_MARKER_EL.content.classList.add("is-active");
            KCSS_ACTIVE_MARKER_EL.zIndex = KCSS_INACTIVE_MARKER_ZINDEX + totalMarkerCount;
        }
    }

    function clearActiveMarker() {
        if (KCSS_ACTIVE_MARKER_EL) {
            greshamMapLog('Clearing active marker');
            KCSS_ACTIVE_MARKER_EL.content.classList.remove("is-active");
            KCSS_ACTIVE_MARKER_EL.zIndex = KCSS_INACTIVE_MARKER_ZINDEX;
        }
        KCSS_ACTIVE_MARKER_EL = null;
        KCSS_INACTIVE_MARKER_ZINDEX = null;
    }

    function bindClearActiveOnOffCanvasClose(offCanvasName) {
        greshamMapLog(`Binding off-canvas close handler for: "${offCanvasName}"`);

        const panel = document.querySelector(
            `.e-off-canvas[aria-label="${CSS.escape(offCanvasName)}"]`
        );

        if (!panel) {
            greshamMapWarn(`Off-canvas panel not found during binding: "${offCanvasName}"`);
            return;
        }

        const obs = new MutationObserver(() => {
            const hidden = panel.getAttribute("aria-hidden");
            if (hidden === "true") {
                greshamMapLog('Off-canvas closed, clearing active marker');
                clearActiveMarker();
            }

        });

        obs.observe(panel, { attributes: true, attributeFilter: ["aria-hidden"] });
        greshamMapLog('Off-canvas observer bound successfully');
    }

    function updateMarkersScale( innerMap, markers, mapElement) {

        let zoom = innerMap.getZoom();
        greshamMapLog(`zoom = ${zoom}`);
        markers.forEach(marker => {
            if(zoom <= 8) {
                marker.style.setProperty(`--kcss-map-marker-scale`, `round(${zoom})`);
                // marker.element.classList.add("is-visible");
            } else {
                // marker.element.classList.remove("is-visible");
                marker.style.setProperty(`--kcss-map-marker-scale`, `round(${zoom})`);
            }

            // if(zoom >= 9) {
            //     mapElement.classList.add( "is-zoom-9" );
            // }
        });
    }

    // let lastActiveMarkerElement = null;

    // ═════════════════════════════════════════════════════════════════════
    // MAIN MAP INITIALIZATION
    // ═════════════════════════════════════════════════════════════════════

    async function initMap() {
        greshamMapLog('='.repeat(70));
        greshamMapLog('INITIALIZING MAP');
        greshamMapLog('='.repeat(70));

        const mapElement = document.querySelector("gmp-map");
        const useMarkers = mapElement.getAttribute("kml-map-markers") || 'true';

        greshamMapLog(`mapElement: ${mapElement}`);
        greshamMapLog(`useMarkers: ${useMarkers}`);

        try {
            await google.maps.importLibrary("maps");
            greshamMapLog('✓ Maps library loaded');

            if(useMarkers) {
                await google.maps.importLibrary("marker");
                greshamMapLog('✓ Marker library loaded');
            }

        } catch (err) {
            greshamMapError('Failed to load Google Maps libraries:', err);
            kam_ui_object?.disable_loading_screen?.();
            return;
        }

        if (!mapElement) {
            greshamMapError('Map element <gmp-map> not found in DOM');
            return;
        }
        greshamMapLog('✓ Map element found');


        let collisionBehavior = google.maps.CollisionBehavior.OPTIONAL_AND_HIDES_LOWER_PRIORITY;

        const innerMap = mapElement.innerMap;
        if (!innerMap) {
            greshamMapError('innerMap property not available');
            return;
        }

        greshamMapLog('✓ innerMap instance available');

        const clusterIconsVisible = mapElement.getAttribute("kml-map-cluster");

        const OFFCANVAS_NAME = mapElement.getAttribute("kml-map-offcanvas-name");

        let MAP_OPTIONS = {
            disableDefaultUI: true,
            clickableIcons: false,
        };

        /*
        * Conditional UI options
        * */
        const hideMapUI = mapElement.getAttribute("kml-map-hide-ui");

        if(hideMapUI === 'false') {
            MAP_OPTIONS = Object.assign(MAP_OPTIONS, {
                cameraControl: true,
                scaleControl: true,
            });
        }

        /* Conditional zoom settings */

        const mapMinZoom = mapElement.getAttribute("kml-map-min-zoom") || 'NULL';

        const mapMaxZoom = mapElement.getAttribute("kml-map-max-zoom");

        if(mapMaxZoom) {
            MAP_OPTIONS = Object.assign(MAP_OPTIONS, {
                minZoom: mapMinZoom,
                maxZoom: mapMaxZoom,
            });
        }

        const boundToUK = mapElement.getAttribute("kml-map-bound");
        greshamMapLog(`boundToUK: ${boundToUK}`);

        const BOUNDS = {
            north: 60,
            south: 49.9,
            west: -8.6,
            east: 1.84
        };

        const UK_PADDING = {
            top: 0,
            right: 0,
            bottom: 0,
            left: 0
        };

        if(boundToUK) {

            MAP_OPTIONS = Object.assign(MAP_OPTIONS, {
                restriction: {
                    latLngBounds: BOUNDS,
                    strictBounds: false
                },
            });

        }

        const LatLngBounds = new google.maps.LatLngBounds(
            { lat: BOUNDS.south, lng: BOUNDS.west },
            { lat: BOUNDS.north, lng: BOUNDS.east }
        );

        innerMap.fitBounds(
            LatLngBounds,
            UK_PADDING,
            mapMinZoom
        );
        greshamMapLog('✓ Initial viewport set to UK bounds');

        greshamMapLog('Map configuration:', MAP_OPTIONS);
        innerMap.setOptions(MAP_OPTIONS);

        if(OFFCANVAS_NAME && useMarkers) {
            bindClearActiveOnOffCanvasClose(OFFCANVAS_NAME);
        }

        if(OFFCANVAS_NAME) {
            greshamMapLog(`Binding off-canvas focus handler: "${OFFCANVAS_NAME}"`);
            const panel = document.querySelector(
                `.e-off-canvas[aria-label="${CSS.escape(OFFCANVAS_NAME)}"]`
            );
            // panel.parentElement.setAttribute(`data-kcss-focus-managed`, `true`);
            greshamMapLog('attribute added to off-canvas');
        }

        greshamMapLog('Fetching GeoJSON data from REST API...');

        kam_ui_object.rest_request(
            "/wp-json/gresham/v1/get-map-file/",
            "GET",
            {},
            function (response) {
                greshamMapLogGroup('GeoJSON Processing', () => {
                    try {
                        greshamMapLog('REST response received');

                        const raw = response?.data?.geojson;
                        if (!raw) {
                            greshamMapWarn('No GeoJSON data in response');
                            return;
                        }
                        greshamMapLog('Raw GeoJSON length:', raw.length, 'characters');

                        let geo;
                        try {
                            geo = JSON.parse(raw);
                            greshamMapLog('✓ GeoJSON parsed successfully');
                        } catch (parseError) {
                            greshamMapError('Failed to parse GeoJSON:', parseError);
                            return;
                        }

                        if (geo?.type !== "FeatureCollection") {
                            greshamMapWarn('Invalid GeoJSON type:', geo?.type);
                            return;
                        }

                        const features = geo.features || [];
                        greshamMapLog(`GeoJSON contains ${features.length} features`);

                        if (!features.length) {
                            greshamMapWarn('GeoJSON FeatureCollection is empty');
                            return;
                        }

                        const pointFeatures = [];
                        const nonPointFeatures = [];

                        for (const f of features) {
                            const t = f?.geometry?.type;
                            if (t === "Point") pointFeatures.push(f);
                            else nonPointFeatures.push(f);
                        }

                        greshamMapLog(`Feature breakdown: ${pointFeatures.length} points, ${nonPointFeatures.length} non-points`);

                        if (nonPointFeatures.length) {
                            greshamMapLog(`Rendering ${nonPointFeatures.length} non-point features`);
                            innerMap.data.addGeoJson({
                                type: "FeatureCollection",
                                features: nonPointFeatures
                            });
                        }

                        greshamMapLog(`Creating ${pointFeatures.length} markers...`);

                        let markerCount = pointFeatures.length;
                        greshamMapLog(`features total: ` + markerCount);

                        if(useMarkers === 'true' && pointFeatures.length > 0) {
                            const markers = pointFeatures
                                .map((f, index) => {
                                    const coords = f?.geometry?.coordinates;
                                    if (!Array.isArray(coords) || coords.length < 2) {
                                        greshamMapWarn(`Invalid coordinates for feature ${index}`);
                                        return null;
                                    }

                                    const [lng, lat] = coords;
                                    if (typeof lat !== "number" || typeof lng !== "number") {
                                        greshamMapWarn(`Non-numeric coordinates for feature ${index}`);
                                        return null;
                                    }


                                    const markerEl = createMarkerElement(f);

                                    const marker = new google.maps.marker.AdvancedMarkerElement({
                                        map: innerMap,
                                        position: {
                                            lat,
                                            lng,
                                        },
                                        title: f?.properties?.Name || "",
                                        content: markerEl,
                                        zIndex: index,
                                        gmpClickable: true,
                                        collisionBehavior: collisionBehavior,
                                    });

                                    if (OFFCANVAS_NAME) {
                                        marker.addListener("gmp-click", () => {
                                            greshamMapLog('Marker clicked:', f?.properties?.Name || 'Unknown');

                                            window.__activeMapMarkerElement = marker.element;
                                            greshamMapLog(window.__activeMapMarkerElement);

                                            marker.element?.focus({ preventScroll: true });

                                            if(innerMap.zoom >= 11 && innerMap.zoom < 15) {
                                                innerMap.zoom = 15;
                                            }

                                            if(innerMap.zoom >= 9 && innerMap.zoom < 11) {
                                                innerMap.zoom = 12;
                                            }

                                            if(innerMap.zoom < 9) {
                                                innerMap.zoom = 9;
                                            }

                                            setActiveMarker(marker, index, markerCount);
                                            updateMapOffCanvas(f, OFFCANVAS_NAME);
                                            updateMarkersScale(innerMap, markers, mapElement);

                                            window.setTimeout(() => {
                                                updateMarkersScale(innerMap, markers);

                                            }, 10);


                                            setTimeout(() => {
                                                greshamMapLog('Opening off-canvas panel');
                                                window.KCSS_OffCanvas?.openByName?.(OFFCANVAS_NAME);
                                            }, 0);
                                            innerMap.panTo({lat, lng});
                                        });

                                        marker.style.setProperty(`--kcss-map-marker-scale`, `round(${innerMap.getZoom()})`);

                                    }



                                    return marker;
                                })
                                .filter(Boolean);

                            greshamMapLog(`✓ Created ${markers.length} valid markers`);

                            if (markers.length && window.markerClusterer?.MarkerClusterer && clusterIconsVisible === 'true') {
                                greshamMapLog('Initializing marker clustering...');

                                const CLUSTER_SIZE = 48;
                                const CLUSTER_STROKE = 2;
                                const CLUSTER_INNER_RING = 5;
                                const CLUSTER_FONT_SIZE = 16;
                                const CLUSTER_COLOR = "var(--e-global-color-3dcf7e7, #0f172a)";
                                const CLUSTER_TEXT_COLOR = "#ffffff";
                                const ALGORITHM_OPTION_RADIUS = 40;

                                const clusterRenderer = {
                                    render({count, position}) {
                                        greshamMapLog(`Rendering cluster: ${count} markers`);

                                        const radius = CLUSTER_SIZE / 2;
                                        const outerRadius = radius - CLUSTER_STROKE;
                                        const innerRadius = radius - CLUSTER_INNER_RING;

                                        const svg = `
<svg xmlns="http://www.w3.org/2000/svg" width="${CLUSTER_SIZE}" height="${CLUSTER_SIZE}" viewBox="0 0 ${CLUSTER_SIZE} ${CLUSTER_SIZE}">
  <circle cx="${radius}" cy="${radius}" r="${outerRadius}" fill="${CLUSTER_COLOR}" opacity="0.92"/>
  <circle cx="${radius}" cy="${radius}" r="${innerRadius}" fill="#ffffff" opacity="0.18"/>
  <text x="50%" y="54%" dominant-baseline="middle" text-anchor="middle" font-size="${CLUSTER_FONT_SIZE}" fill="${CLUSTER_TEXT_COLOR}">${count}</text>
</svg>
                                    `.trim();

                                        const wrap = document.createElement("div");
                                        wrap.innerHTML = svg;

                                        return new google.maps.marker.AdvancedMarkerElement({
                                            position,
                                            content: wrap,
                                            zIndex: 1000 + count
                                        });
                                    }
                                };

                                new markerClusterer.MarkerClusterer({
                                    map: innerMap,
                                    markers,
                                    renderer: clusterRenderer,
                                    algorithmOptions: {radius: ALGORITHM_OPTION_RADIUS},
                                    onClusterClick: (event, cluster, map) => {
                                        greshamMapLog('Cluster clicked, zooming to bounds');
                                        map.fitBounds(cluster.bounds);
                                        map.panTo({lat, lng});
                                    }
                                });

                                greshamMapLog('✓ Clustering initialized');
                            } else if (markers.length && !window.markerClusterer?.MarkerClusterer) {
                                greshamMapWarn('MarkerClusterer library not found');
                            }

                            innerMap.addListener('zoom_changed', () => {
                                updateMarkersScale(innerMap, markers, mapElement);
                                // if(innerMap.zoom <= 11) {
                                //     mapElement.style.setProperty( `--kcss-map-marker-font-size`, `clamp(2.4rem, 0.571rem + 2.381vw, 4rem)` );
                                // } else {
                                //     mapElement.style.setProperty( `--kcss-map-marker-font-size`, `clamp(1.6rem, -0.229rem + 2.381vw, 3.2rem)` );
                                // }
                            });
                        }


                        greshamMapLog('='.repeat(70));
                        greshamMapLog('MAP INITIALIZATION COMPLETE');
                        greshamMapLog('='.repeat(70));

                    } catch (err) {
                        greshamMapError('Error processing map data:', err);
                    } finally {
                        kam_ui_object?.disable_loading_screen?.();
                    }
                });
            },
            jwt.get_map_file_jwt
        );

        // 3. Prevent FOUC: Wait for 'idle' before showing the container
        google.maps.event.addListenerOnce(innerMap, 'idle', () => {
            greshamMapLog('Map is ready');
            // mapElement.style.visibility = 'visible';
            mapElement.classList.add('fade-in'); // Optional CSS animation
        });

    }

    greshamMapLog('Calling initMap()...');
    initMap();
});